import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '../../components/auth/use-auth';
import { Logo } from '../../components/ui/logo';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { QRCodeSVG } from 'qrcode.react';
import { zielNachLogin } from '../../lib/auth/zielNachLogin';

interface LoginFormData {
  email: string;
  password: string;
  remember?: boolean;
}

const Login = () => {
  const {
    login,
    mfaPending,
    verifyMfa,
    resendMfaEmail,
    loadMfaEnrollment,
    completeMfaEnrollment,
    isAuthenticated,
    isLoading
  } = useAuth();
  
  const navigate = useNavigate();
  const ziel = zielNachLogin(useLocation().state);
  const [loginError, setLoginError] = useState<string>('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaMethod, setMfaMethod] = useState<'totp' | 'email'>('totp');
  const [emailCodeChallengeId, setEmailCodeChallengeId] = useState<string | null>(null);
  const [setupSecret, setSetupSecret] = useState('');
  const [setupOtpAuthUrl, setSetupOtpAuthUrl] = useState('');
  const [setupCode, setSetupCode] = useState('');
  const [isSetupLoading, setIsSetupLoading] = useState(false);
  
  const {
    register,
    handleSubmit,
    formState: {
      errors,
      isSubmitting
    }
  } = useForm<LoginFormData>();

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate(ziel);
    }
  }, [isAuthenticated, navigate, ziel]);

  const onSubmit = async (data: LoginFormData) => {
    setLoginError('');
    try {
      const result = await login(data.email, data.password, !!data.remember);
      if (!result.requiresMfa) {
        navigate(ziel);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Anmeldung fehlgeschlagen';
      setLoginError(errorMessage);
    }
  };

  const setupChallengeId = mfaPending?.requiresSetup ? mfaPending.challengeId : null;

  useEffect(() => {
    if (!setupChallengeId) return;
    let active = true;
    setIsSetupLoading(true);
    loadMfaEnrollment()
      .then((data) => {
        if (!active) return;
        setSetupSecret(data.setup.secret);
        setSetupOtpAuthUrl(data.setup.otpauthUrl);
      })
      .catch((error) => {
        const errorMessage = error instanceof Error ? error.message : 'MFA-Setup konnte nicht geladen werden';
        setLoginError(errorMessage);
      })
      .finally(() => {
        if (active) setIsSetupLoading(false);
      });
    return () => {
      active = false;
    };
    // Einmal je Einrichtungs-Challenge laden; die Provider-Funktion ist nicht memoisiert.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setupChallengeId]);

  const handleVerifyMfa = async () => {
    setLoginError('');
    try {
      await verifyMfa(mfaMethod, mfaCode);
      navigate(ziel);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'MFA-Bestätigung fehlgeschlagen';
      setLoginError(errorMessage);
    }
  };

  const sendMfaEmailCode = async () => {
    setLoginError('');
    try {
      await resendMfaEmail();
      setEmailCodeChallengeId(mfaPending?.challengeId ?? null);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'E-Mail-Code konnte nicht versendet werden';
      setLoginError(errorMessage);
    }
  };

  const selectEmailMethod = () => {
    setMfaMethod('email');
    // Der Login verschickt keinen Code; er wird erst bei Wahl der E-Mail-Methode angefordert.
    if (mfaPending?.challengeId && emailCodeChallengeId !== mfaPending.challengeId) {
      void sendMfaEmailCode();
    }
  };

  const handleEnableMfa = async () => {
    setLoginError('');
    try {
      await completeMfaEnrollment(setupCode);
      navigate(ziel);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'MFA konnte nicht aktiviert werden';
      setLoginError(errorMessage);
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background p-4">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <Logo size="lg" />
          </div>
          <h2 className="mt-2 text-xl font-semibold">Anmelden</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Gib deine Anmeldedaten ein, um auf dein Musikschul-Dashboard
            zuzugreifen
          </p>
        </div>

        {!mfaPending && (
        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-4 rounded-md">
            <div>
              <label htmlFor="email" className="block text-sm font-medium">
                E-Mail-Adresse
              </label>
              <Input 
                id="email" 
                type="email" 
                autoComplete="email" 
                {...register('email', {
                  required: 'E-Mail ist erforderlich',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Ungültige E-Mail-Adresse'
                  }
                })} 
                className={errors.email ? 'border-destructive' : ''} 
              />
              {errors.email && (
                <p className="mt-1 text-xs text-destructive">
                  {errors.email.message}
                </p>
              )}
            </div>
            
            <div>
              <label htmlFor="password" className="block text-sm font-medium">
                Passwort
              </label>
              <Input 
                id="password" 
                type="password" 
                autoComplete="current-password" 
                {...register('password', {
                  required: 'Passwort ist erforderlich'
                })} 
                className={errors.password ? 'border-destructive' : ''} 
              />
              {errors.password && (
                <p className="mt-1 text-xs text-destructive">
                  {errors.password.message}
                </p>
              )}
            </div>
          </div>

          {/* Fehlermeldung anzeigen */}
          {loginError && (
            <div className="rounded-md bg-destructive/10 p-3">
              <p className="text-sm text-destructive text-center">
                {loginError}
              </p>
            </div>
          )}

          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <input 
                id="remember-me" 
                {...register('remember')}
                type="checkbox" 
                className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary" 
              />
              <label htmlFor="remember-me" className="ml-2 block text-sm text-muted-foreground">
                Angemeldet bleiben
              </label>
            </div>
            <div className="text-sm">
              <Link to="/forgot-password" className="font-medium text-primary hover:underline">
                Passwort vergessen?
              </Link>
            </div>
          </div>

          <div className="space-y-3">
            <Button 
              type="submit" 
              className="w-full" 
              disabled={isSubmitting || isLoading}
            >
              {isSubmitting ? 'Anmeldung...' : 'Anmelden'}
            </Button>
          </div>
        </form>
        )}

        {mfaPending && (
          <div className="mt-8 space-y-6">
            <div className="rounded-md border p-4">
              <h3 className="text-base font-semibold">Multi-Faktor-Authentifizierung</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                {mfaPending.requiresSetup
                  ? 'Für dein Konto ist MFA verpflichtend. Bitte richte TOTP jetzt ein.'
                  : 'Bitte bestätige deine Anmeldung mit einem MFA-Code.'}
              </p>
            </div>

            {mfaPending.requiresSetup && (
              <div className="space-y-3 rounded-md border p-4">
                <h4 className="text-sm font-semibold">1) TOTP in Authenticator-App einrichten</h4>
                {isSetupLoading ? (
                  <p className="text-sm text-muted-foreground">Setup wird geladen…</p>
                ) : (
                  <>
                    {setupOtpAuthUrl ? (
                      <div className="inline-block rounded-md border bg-white p-2">
                        <QRCodeSVG value={setupOtpAuthUrl} size={168} />
                      </div>
                    ) : null}
                    <p className="text-sm text-muted-foreground break-all">
                      Secret: <span className="font-mono">{setupSecret || '—'}</span>
                    </p>
                    <p className="text-xs text-muted-foreground break-all">
                      OTPAuth URL: <span className="font-mono">{setupOtpAuthUrl || '—'}</span>
                    </p>
                    <Input
                      value={setupCode}
                      onChange={(e) => setSetupCode(e.target.value)}
                      placeholder="6-stelliger TOTP-Code"
                    />
                    <Button type="button" className="w-full" onClick={handleEnableMfa} disabled={isLoading || !setupCode}>
                      MFA jetzt aktivieren
                    </Button>
                  </>
                )}
              </div>
            )}

            {!mfaPending.requiresSetup && (
              <div className="space-y-3 rounded-md border p-4">
                <h4 className="text-sm font-semibold">MFA-Code bestätigen</h4>
                <div className="flex items-center gap-2">
                  {mfaPending.methods.totp && (
                    <Button type="button" variant={mfaMethod === 'totp' ? 'default' : 'outline'} onClick={() => setMfaMethod('totp')}>
                      TOTP
                    </Button>
                  )}
                  {mfaPending.methods.email && (
                    <Button type="button" variant={mfaMethod === 'email' ? 'default' : 'outline'} onClick={selectEmailMethod}>
                      E-Mail-Code
                    </Button>
                  )}
                </div>
                <Input
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value)}
                  placeholder="6-stelliger Code"
                />
                <div className="flex gap-2">
                  <Button type="button" className="flex-1" onClick={handleVerifyMfa} disabled={isLoading || !mfaCode}>
                    Code bestätigen
                  </Button>
                  {mfaMethod === 'email' && (
                    <Button type="button" variant="outline" onClick={() => void sendMfaEmailCode()} disabled={isLoading}>
                      {emailCodeChallengeId === mfaPending.challengeId ? 'Code neu senden' : 'Code senden'}
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Login;
